/* ============================================================================
   product-card-swatch-media.js, Hemlock & Oak

   WHAT
   Tapping a colour swatch on a product card swaps that card's photo to the
   chosen colour. snippets/option-value.liquid already renders
   data-variant-media (a JSON dump of variant.featured_media) on each swatch
   radio; nothing was reading it.

   WHY NOT IN custom.js
   custom.js block 2 (Product card interactions) is the natural home. It was
   left alone because the Admin API replaces whole files rather than patching,
   and custom.js is 70KB of order-integrity code (duo pair enforcement, the
   HEADSTART watcher). Fold this into block 2 next time custom.js is edited
   via the Shopify CLI, then delete this file and its tag in
   layout/theme.liquid.

   ----------------------------------------------------------------------------
   WHY THIS FILE IS SHAPED THE WAY IT IS
   ----------------------------------------------------------------------------
   Four separate problems stacked on each other. Every one of them is still
   live, so removing any of these four measures brings the bug back.

   1. NOTHING CONSUMED THE DATA. data-variant-media has always been rendered
      on every swatch radio, and no code read it. custom.js block 2 reads
      data-variant-id to update the quick-buy input and stops there. Verified
      identical in every theme snapshot back to 2026-06-26, so this was never
      a regression, it simply never worked.

   2. WEBKIT WILL NOT RE-SELECT A NEW srcset. Rewriting srcset works in Blink
      and silently does nothing in WebKit on an image that has already
      finished loading, so the phone kept painting the old candidate while
      desktop looked fine. srcset and sizes are therefore REMOVED and one
      explicit src is set: with no candidate list there is nothing to
      reselect. Do not "improve" this back into a responsive srcset.

   3. THE IMAGE IS DETACHED AND REBUILT. A few hundred ms after a swatch
      change something replaces the card's img and renders the DEFAULT colour
      (caught by image.isConnected going false). A one-shot swap cannot
      survive that however correct it is, so the choice is recorded in a
      registry keyed by the swatch group's input NAME, which outlives the
      nodes, and re-asserted on a debounced observer plus a 150/500/1200ms
      timer ladder. The ladder is not belt-and-braces: on-device logs caught a
      rebuild the observer missed that only t150 recovered.

   4. THE FIGURE STACKS TWO IMAGES AND BOTH ARE PAINTED. product-card.liquid
      renders the primary image and then the secondary hover image. On-device
      measurement (2026-08-15) showed both at the same 199px width, both
      opacity 1, both display:block, both visible, with the secondary later in
      the DOM and therefore on top. Writing only to
      .product-card__image--primary updated a layer sitting underneath a
      picture of the old colour, which is exactly how a correct swap can look
      like a broken one. This writes to EVERY img in .product-card__figure.
      That is also the right behaviour on its own merits: the hover image now
      matches the chosen colour instead of flashing the original.

   STILL UNEXPLAINED, worth chasing separately: what rebuilds the image in
   point 3, and why it renders the default colour. Most likely the theme's own
   variant-swap machinery misfiring. This file rides over it rather than
   repairing it.

   DEBUG
   Add ?swatchdebug=1 to any collection URL for an on-screen log panel: every
   img in the figure with its class, size, opacity and display, plus each
   choice and re-assert. Renders ONLY with that parameter, so a customer can
   never see it. Deliberately kept for now because the point 3 rebuild is
   still unexplained; strip this block once that is understood.
   ============================================================================ */
(function () {
  if (window.__hoCardSwatchMedia) return;
  window.__hoCardSwatchMedia = true;

  var CARD_IMAGE_WIDTHS = [200, 300, 400, 600, 800, 1200, 1800];
  var SWATCH_SELECTOR = '.product-card__swatch-list [type="radio"], .product-card__variant-list [type="radio"]';
  var FIGURE_IMAGES = ".product-card__figure img";
  var DEBUG = window.location.search.indexOf("swatchdebug") !== -1;

  /* Chosen image per swatch group. Keyed by input name, NOT by element:
     the elements are exactly what gets thrown away. See point 3. */
  var chosen = {};
  var chosenCount = 0;
  var described = {};

  /* --- debug panel --------------------------------------------------------- */

  var panel = null;

  function log(message) {
    if (!DEBUG) return;
    try {
      if (!panel) {
        panel = document.createElement("div");
        panel.setAttribute("style", [
          "position:fixed", "left:0", "right:0", "top:0", "z-index:2147483647",
          "max-height:40vh", "overflow:auto", "background:rgba(0,0,0,0.92)",
          "color:#9f9", "font:11px/1.35 ui-monospace,Menlo,monospace",
          "padding:8px", "white-space:pre-wrap", "word-break:break-all"
        ].join(";"));
        (document.body || document.documentElement).appendChild(panel);
      }
      panel.textContent += message + "\n";
      panel.scrollTop = panel.scrollHeight;
    } catch (err) { /* debugging must never break the page */ }
  }

  /* One-time dump of what is actually inside the figure. This is the question
     that took three rounds to answer: the src was being set correctly on
     .product-card__image--primary and the screen still did not change. */
  function describe(card, key) {
    if (!DEBUG || described[key]) return;
    described[key] = true;
    var images = card.querySelectorAll(FIGURE_IMAGES);
    log("  FIGURE holds " + images.length + " img");
    for (var i = 0; i < images.length; i++) {
      var im = images[i];
      var cs = window.getComputedStyle(im);
      log("   #" + i + " " + String(im.className || "(none)").slice(0, 40));
      log("      w=" + im.clientWidth + " op=" + cs.opacity + " disp=" + cs.display + " vis=" + cs.visibility);
    }
    var figure = card.querySelector(".product-card__figure");
    if (figure) {
      log("   figure bg=" + String(window.getComputedStyle(figure).backgroundImage).slice(0, 44));
    }
  }

  /* --- url helpers --------------------------------------------------------- */

  function sizedUrl(src, width) {
    try {
      var url = new URL(src, window.location.href);
      url.searchParams.set("width", String(width));
      return url.href;
    } catch (err) {
      return src;
    }
  }

  /* Smallest candidate that still covers the element at this pixel ratio. */
  function bestWidth(image) {
    var rendered = image.clientWidth || image.getBoundingClientRect().width || 0;
    var target = rendered * (window.devicePixelRatio || 1);
    if (!target) return CARD_IMAGE_WIDTHS[CARD_IMAGE_WIDTHS.length - 1];
    for (var i = 0; i < CARD_IMAGE_WIDTHS.length; i++) {
      if (CARD_IMAGE_WIDTHS[i] >= target) return CARD_IMAGE_WIDTHS[i];
    }
    return CARD_IMAGE_WIDTHS[CARD_IMAGE_WIDTHS.length - 1];
  }

  /* --- applying ------------------------------------------------------------ */

  /* EVERY img in the figure, not just the first .product-card__image--primary.
     See point 4: the secondary sits on top of the primary at full opacity, so
     writing only the primary paints a layer nobody can see. */
  function applyTo(card, src) {
    var images = card.querySelectorAll(FIGURE_IMAGES);
    if (!images.length) return false;

    var changed = false;
    for (var i = 0; i < images.length; i++) {
      var image = images[i];
      var next = sizedUrl(src, bestWidth(image));
      if (image.getAttribute("src") === next) continue;

      /* Clear the responsive attributes BEFORE setting src, so the selection
         that runs on the src assignment has no candidate list to prefer.
         See point 2. */
      image.removeAttribute("srcset");
      image.removeAttribute("sizes");
      image.src = next;
      changed = true;
    }
    return changed;
  }

  /* Re-assert every recorded choice. Inert until the first swatch tap. */
  function reapplyAll(reason) {
    if (!chosenCount) return;
    var inputs = document.querySelectorAll(SWATCH_SELECTOR);
    var done = {};
    for (var i = 0; i < inputs.length; i++) {
      var name = inputs[i].name;
      if (!name || done[name] || !chosen[name]) continue;
      done[name] = true;
      var card = inputs[i].closest("product-card");
      if (card && applyTo(card, chosen[name])) {
        log("re-applied (" + reason + ") " + name.slice(-20));
      }
    }
  }

  /* --- recording a choice --------------------------------------------------- */

  function chooseFromInput(input, via) {
    if (!input || !input.dataset || !input.dataset.variantMedia) return;
    if (!input.name) return;

    var media;
    try {
      media = JSON.parse(input.dataset.variantMedia);
    } catch (err) {
      return;
    }
    var src = media && (media.src || (media.preview_image && media.preview_image.src));
    if (!src) return;

    var card = input.closest("product-card");
    if (!card) return;

    if (!chosen[input.name]) chosenCount++;
    chosen[input.name] = src;

    describe(card, input.name);
    applyTo(card, src);
    log("[" + via + "] chose " + src.slice(-30));

    /* The rebuild lands a few hundred ms later and is not always a mutation
       the observer catches in time, so re-assert on a short ladder too. Cheap:
       each pass is a no-op once the src already matches. */
    setTimeout(function () { reapplyAll("t150"); }, 150);
    setTimeout(function () { reapplyAll("t500"); }, 500);
    setTimeout(function () { reapplyAll("t1200"); }, 1200);
  }

  /* --- triggers ------------------------------------------------------------ */

  document.addEventListener("change", function (event) {
    var radio = event.target;
    if (!(radio instanceof HTMLInputElement)) return;
    if (!radio.matches(SWATCH_SELECTOR)) return;
    chooseFromInput(radio, "change");
  });

  /* Second route in case change never fires: the swatch fieldset carries
     -webkit-overflow-scrolling: touch on mobile, which has a history of
     swallowing taps on iOS. Resolves the input from the label's for attribute
     rather than from :checked, so it does not matter whether the radio has
     updated yet. Both routes are idempotent. */
  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var label = target.closest(".product-card__swatch-list label[for], .product-card__variant-list label[for]");
    if (!label) return;
    chooseFromInput(document.getElementById(label.getAttribute("for")), "click");
  }, true);

  /* --- show-more "+" fallback ------------------------------------------------
     custom.js block 2 owns the primary click handler for .show-more-button.
     The button sits INSIDE the same -webkit-overflow-scrolling: touch fieldset
     as the labels above, so its tap-generated click can be swallowed the same
     way, which reads as a dead "+" on whichever card is tapped after another
     card's row has been expanded or scrolled (reported 2026-08-16: expanding
     two swatch groups side by side, the second "+" did nothing). pointerup is
     still delivered when the synthesized click is not, so mirror the reveal
     here. The pointerdown distance guard keeps a drag-release over the button
     from expanding it: a real scroll gesture must not count as a tap. Both
     routes are idempotent: whichever runs second finds the button already
     removed (checked after an 80ms grace so the normal click route wins the
     common case) and does nothing. Fold into custom.js block 2 with the rest
     of this file. */
  var showMorePressed = null;
  var showMorePressX = 0;
  var showMorePressY = 0;

  document.addEventListener("pointerdown", function (event) {
    var target = event.target;
    showMorePressed = target instanceof Element ? target.closest(".show-more-button") : null;
    showMorePressX = event.clientX;
    showMorePressY = event.clientY;
  }, true);

  document.addEventListener("pointerup", function (event) {
    if (!showMorePressed) return;
    var button = showMorePressed;
    showMorePressed = null;

    var target = event.target;
    if (!(target instanceof Element) || target.closest(".show-more-button") !== button) return;
    if (Math.abs(event.clientX - showMorePressX) > 10) return;
    if (Math.abs(event.clientY - showMorePressY) > 10) return;

    var list = button.closest(".product-card__swatch-list");
    if (!list) return;

    setTimeout(function () {
      if (!button.isConnected) return; /* the click route already handled it */
      list.querySelectorAll(".hidden-swatch").forEach(function (swatch) {
        swatch.style.display = "inline-flex";
      });
      button.remove();
    }, 80);
  }, true);

  /* --- the observer that makes it stick ------------------------------------- */

  /* Debounced because custom.js block 1 runs its own body-wide observer that
     rewrites Judge.me stars, so mutations are frequent and bursty. */
  var timer = null;
  var observer = new MutationObserver(function () {
    if (!chosenCount) return;
    clearTimeout(timer);
    timer = setTimeout(function () { reapplyAll("observer"); }, 120);
  });

  function boot() {
    observer.observe(document.body, { childList: true, subtree: true });
    if (DEBUG) {
      window.addEventListener("error", function (event) { log("! JS error: " + event.message); });
      log("loaded v5. debug panel at TOP of screen");
      log("tap a swatch to see the figure dump and re-asserts");
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

/* === SPEC GLOSSARY POPOVER (2026-08-16) =====================================
   Tap a spec line item on a product card (e.g. "HERITAGE 120GSM") to open a
   small definition popover. The definitions travel ON the markup:
   snippets/product-spec-line.liquid looks each spec up (downcased) in the
   shop-level JSON metafield custom.spec_glossary and, on a hit, adds
   .product-spec-line__item--info plus data-spec-info="...". This block only
   READS those attributes, so there is no JSON blob to fetch and terms without
   definitions never become tappable. Scoped to .product-card__specs: the same
   snippet renders on the product page and wishlist rows, where the attributes
   stay inert.

   Edit definitions in admin: Settings -> Custom data -> Shop -> Spec glossary
   (keys lowercase). Styles: "SPEC GLOSSARY POPOVER" section of custom.css.

   Separate IIFE below the swatch-media one on purpose: no shared state, its
   own idempotence guard, and it folds into custom.js block 2 together with
   the rest of this file whenever that merge happens. */
(function () {
  if (window.__hoSpecGlossary) return;
  window.__hoSpecGlossary = true;

  var ITEM_SELECTOR = ".product-card__specs .product-spec-line__item--info";

  var pop = null;
  var openFor = null;

  function closePop() {
    if (pop && pop.parentNode) pop.parentNode.removeChild(pop);
    pop = null;
    openFor = null;
  }

  function openPop(item) {
    closePop();

    var info = item.getAttribute("data-spec-info");
    if (!info) return;

    pop = document.createElement("div");
    pop.className = "tm-spec-pop";
    pop.setAttribute("role", "note");

    var term = document.createElement("p");
    term.className = "tm-spec-pop__term";
    term.textContent = item.textContent.trim();

    var body = document.createElement("p");
    body.className = "tm-spec-pop__body";
    body.textContent = info;

    pop.appendChild(term);
    pop.appendChild(body);
    document.body.appendChild(pop);

    /* Position: centred above the item, clamped to the viewport, flipped
       below when there is no headroom. position: fixed in CSS, so
       getBoundingClientRect coordinates are used as-is. */
    var itemRect = item.getBoundingClientRect();
    var popRect = pop.getBoundingClientRect();
    var left = itemRect.left + itemRect.width / 2 - popRect.width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popRect.width - 8));
    var top = itemRect.top - popRect.height - 8;
    if (top < 8) top = itemRect.bottom + 8;
    pop.style.left = Math.round(left) + "px";
    pop.style.top = Math.round(top) + "px";

    openFor = item;
  }

  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var item = target.closest(ITEM_SELECTOR);
    if (item) {
      event.preventDefault();
      if (openFor === item) {
        closePop(); /* second tap on the same term closes it */
      } else {
        openPop(item);
      }
      return;
    }

    if (pop && !pop.contains(target)) closePop();
  });

  /* The items carry role="button" tabindex="0" from the Liquid, so mirror
     the tap behaviour for Enter and Space. */
  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") {
      closePop();
      return;
    }
    if (event.key !== "Enter" && event.key !== " ") return;
    var target = event.target;
    if (!(target instanceof Element) || !target.matches(ITEM_SELECTOR)) return;
    event.preventDefault();
    if (openFor === target) {
      closePop();
    } else {
      openPop(target);
    }
  });

  /* The popover is fixed-position, so any scroll or resize would leave it
     floating over the wrong spot. Cheapest correct answer: close it. */
  window.addEventListener("scroll", closePop, true);
  window.addEventListener("resize", closePop);
})();
